import React, { memo } from 'react';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { format } from 'date-fns';
import {
  Person as PersonIcon,
  Build as ToolIcon,
  Hotel as HotelIcon,
  SmartToy as AiIcon,
} from '@mui/icons-material';
import { ChatMessage } from '../../../types/chat';
import InsightCard from './InsightCard';
import InvestmentScoreCard from './InvestmentScoreCard';
import TypingIndicator from './TypingIndicator';
import { transformToolResult } from './utils/ToolResultsTransformer';
import { colors } from '../../utils/designSystem';

interface MessageProps {
  message: ChatMessage;
  isLast?: boolean;
}

const Message: React.FC<MessageProps> = memo(({ message, isLast }) => {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming && isLast;
  const hasToolCalls = message.toolCalls && message.toolCalls.length > 0;
  const hasInvestmentScore = !!message.investmentScore;

  const messageVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: 'easeOut',
      },
    },
  };

  // Show typing indicator when streaming with no content
  if (isStreaming && !message.content) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={messageVariants}
      >
        <Box sx={{ display: 'flex', gap: 2, mb: 3, px: { xs: 2, md: 3 } }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.primary[500],
              flexShrink: 0,
            }}
          >
            <AiIcon sx={{ fontSize: 18, color: 'white' }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <TypingIndicator />
          </Box>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={messageVariants}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          gap: 2,
          mb: 3,
          px: { xs: 2, md: 3 },
        }}
      >
        {/* Avatar */}
        {isUser ? (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.primary[500],
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ fontSize: 18, color: 'white' }} />
          </Avatar>
        ) : (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: colors.primary[500],
              flexShrink: 0,
            }}
          >
            <AiIcon sx={{ fontSize: 18, color: 'white' }} />
          </Avatar>
        )}

        {/* Message Content */}
        <Box sx={{ flex: 1, maxWidth: '75%', minWidth: 0 }}>
          {/* Sender Label */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 0.75,
              fontWeight: 600,
              color: colors.gray[500],
              textAlign: isUser ? 'right' : 'left',
            }}
          >
            {isUser ? 'You' : 'AI Assistant'}
          </Typography>

          {/* Hotel Context Badge */}
          {message.metadata?.hotelName && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              mb: 0.5,
              bgcolor: colors.primary[50],
              borderRadius: 1,
              border: `1px solid ${colors.primary[100]}`,
            }}>
              <HotelIcon sx={{ fontSize: 12, color: colors.primary[500] }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: colors.primary[700], fontWeight: 500 }}>
                {message.metadata.hotelName}
              </Typography>
            </Box>
          )}

          {/* Message Bubble */}
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: isUser ? colors.primary[500] : 'white',
              color: isUser ? 'white' : colors.gray[900],
              borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
              border: isUser ? 'none' : `1px solid ${colors.gray[200]}`,
              position: 'relative',
              ...(isStreaming && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 12,
                  right: isUser ? 'auto' : 12,
                  left: isUser ? 12 : 'auto',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: colors.success[500],
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.5, transform: 'scale(0.8)' },
                  },
                },
              }),
            }}
          >
            {/* Message content with clean markdown */}
            <Box
              sx={{
                '& p': {
                  margin: 0,
                  lineHeight: 1.5,
                  fontSize: '0.8125rem',
                },
                '& p:not(:last-child)': { mb: 1.5 },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 2,
                  mb: 0.75,
                  color: 'inherit',
                  fontWeight: 600,
                  lineHeight: 1.3,
                },
                '& h1': { fontSize: '1.1rem' },
                '& h2': { fontSize: '1rem' },
                '& h3': { fontSize: '0.9375rem' },
                '& ul, & ol': {
                  pl: 2.5,
                  mb: 1.5,
                  '& li': {
                    mb: 0.5,
                    lineHeight: 1.5,
                    fontSize: '0.8125rem',
                  },
                },
                '& blockquote': {
                  pl: 2,
                  ml: 0,
                  borderLeft: '4px solid',
                  borderColor: isUser ? 'rgba(255,255,255,0.3)' : colors.primary[500],
                  fontStyle: 'italic',
                  my: 2,
                  color: isUser ? 'rgba(255,255,255,0.9)' : colors.gray[500],
                },
                '& code': {
                  bgcolor: isUser ? 'rgba(255,255,255,0.15)' : colors.gray[100],
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.75,
                  fontSize: '0.85em',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                },
                '& pre': {
                  my: 2,
                  overflow: 'auto',
                  borderRadius: 2,
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  my: 1.5,
                  fontSize: '0.775rem',
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: isUser ? 'rgba(255,255,255,0.2)' : colors.gray[200],
                  p: 1.25,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: isUser ? 'rgba(255,255,255,0.1)' : colors.gray[50],
                  fontWeight: 600,
                },
                '& strong': { fontWeight: 600 },
                '& a': {
                  color: isUser ? 'white' : colors.primary[500],
                  textDecoration: 'underline',
                },
              }}
            >
              {message.content ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code: (codeProps) => {
                      const { className, children, ...rest } = codeProps;
                      const match = /language-(\w+)/.exec(className || '');
                      const language = match ? match[1] : '';
                      const isInline = !match;

                      if (!isInline && match) {
                        return (
                          <SyntaxHighlighter
                            style={oneDark}
                            language={language}
                            PreTag="div"
                            customStyle={{
                              margin: 0,
                              borderRadius: '8px',
                              fontSize: '0.8125rem',
                            }}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        );
                      }
                      return (
                        <code className={className} {...rest}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              ) : isStreaming ? (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', opacity: 0.7 }}
                >
                  Thinking...
                </Typography>
              ) : null}
            </Box>
          </Paper>

          {/* Timestamp and Tools Used */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 0.75,
              px: 0.5,
              flexWrap: 'wrap',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.gray[400],
                fontSize: '0.7rem',
              }}
            >
              {format(message.timestamp, 'HH:mm')}
            </Typography>

            {/* Tools Used Indicator */}
            {!isUser && hasToolCalls && message.toolCalls && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  bgcolor: colors.gray[100],
                  borderRadius: 1,
                }}
              >
                <ToolIcon sx={{ fontSize: 12, color: colors.gray[500] }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.gray[500],
                    fontSize: '0.65rem',
                    fontWeight: 500,
                  }}
                >
                  {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''} used
                </Typography>
              </Box>
            )}
          </Box>

          {/* AI Insights Section - Only for assistant with tools/scores */}
          {!isUser && (hasToolCalls || hasInvestmentScore) && (
            <Box sx={{ mt: 2.5 }}>
              {/* Investment Score Card (most important - show first) */}
              {hasInvestmentScore && message.investmentScore && (
                <Box sx={{ mb: 2 }}>
                  <InvestmentScoreCard investmentScore={message.investmentScore} />
                </Box>
              )}

              {/* Tool Results as InsightCards */}
              {hasToolCalls && message.toolCalls && (
                <Box>
                  {/* Tools Used Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gray[500],
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.65rem',
                      }}
                    >
                      Tools Executed:
                    </Typography>
                    {message.toolCalls.map((toolCall, index) => {
                      const insight = transformToolResult(toolCall);
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            bgcolor: colors.success[50],
                            color: colors.success[700],
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: colors.success[500],
                            }}
                          />
                          {insight.title}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Insight Cards */}
                  {message.toolCalls.map((toolCall, index) => {
                    const insight = transformToolResult(toolCall);
                    return (
                      <InsightCard
                        key={index}
                        type={insight.type}
                        title={insight.title}
                        summary={insight.summary}
                        metrics={insight.metrics}
                        rawData={insight.rawData}
                        action={insight.action}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.toolCalls === nextProps.message.toolCalls &&
    prevProps.message.investmentScore === nextProps.message.investmentScore &&
    prevProps.isLast === nextProps.isLast
  );
});

Message.displayName = 'Message';

export default Message;
